/**
 * Bundle URL Manager
 * Manages URL parameters for bundle builder page
 */

class BundleURLManager {
  constructor() {
    this.debugMode = false;
    this.SELECTORS = {
      quantityInput: 'input[name="biscuits-bundles-input-quantity"]',
      step: '.biscuits-bundle-step',
      item: '.biscuits-bundle-item',
      quantity: '.biscuits-bundle-quantity',
      plus: '.biscuits-bundle-quantity__button--plus',
      minus: '.biscuits-bundle-quantity__button--minus',
      form: 'biscuits-bundle-form'
    };
    this.debouncedUpdateURL = this.debounce(() => this.updateURL(), 300);
    this.urlParams = new URLSearchParams(window.location.search);
    this.bundleForm = null;
    this.isInitialized = false;
    this.translations = this.getTranslations();

    document.addEventListener('BiscuitsBundleForm:ready', (event) => {
      this.bundleForm = event.target;
      this.init();
    });

    setTimeout(() => {
      if (!this.isInitialized) {
        this.bundleForm = document.querySelector(this.SELECTORS.form);
        if (this.bundleForm) {
          this.init();
        }
      }
    }, 2000);
  }

  getTranslations() {
    const locale = document.documentElement.lang || 'en';
    const translationsValues = JSON.parse(document.getElementById('biscuitsCopyButtonTranslations').textContent);

    const translations = {
      en: {
        shareButton: `${translationsValues.en.shareButton}`,
        selectProducts: `${translationsValues.en.selectProducts}`,
        copying: `${translationsValues.en.copying}`,
        copied: `${translationsValues.en.copied}`,
        copyError: `${translationsValues.en.copyError}`
      },
      de: {
        shareButton: `${translationsValues.de.shareButton}`,
        selectProducts: `${translationsValues.de.selectProducts}`,
        copying: `${translationsValues.de.copying}`,
        copied: `${translationsValues.de.copied}`,
        copyError: `${translationsValues.de.copyError}`
      }
    };

    return translations[locale] || translations.en;
  }

  init() {
    if (this.isInitialized || !this.bundleForm) return;

    this.isInitialized = true;
    this.setupEventListeners();
    this.addShareButton();

    // Wait for all bundle elements to be ready
    this.waitForBundleReady().then(() => {
      this.restoreSelectionFromURL();
    });
  }

  waitForBundleReady() {
    return new Promise((resolve) => {
      const checkReady = () => {
        const quantityInputs = this.bundleForm.querySelectorAll(this.SELECTORS.quantityInput);
        const plusButtons = this.bundleForm.querySelectorAll(this.SELECTORS.plus);

        if (quantityInputs.length > 0 && plusButtons.length > 0) {
          this.debug('Bundle elements are ready');
          resolve();
        } else {
          this.debug('Waiting for bundle elements...');
          setTimeout(checkReady, 500);
        }
      };

      setTimeout(checkReady, 1000);
    });
  }

  setupEventListeners() {
    document.querySelectorAll(this.SELECTORS.step).forEach((step) => {
      step.addEventListener('biscuits--step-update', this.debouncedUpdateURL);
    });

    document.querySelectorAll(this.SELECTORS.item).forEach((item) => {
      item.addEventListener('biscuits--product-selection', this.debouncedUpdateURL);
    });

    document.querySelectorAll(this.SELECTORS.quantity).forEach((quantity) => {
      quantity.addEventListener('biscuits--quantity-updated', this.debouncedUpdateURL);
    });

    this.bundleForm.addEventListener('change', (e) => {
      if (e.target.name === 'biscuits-bundles-input-quantity') {
        this.debouncedUpdateURL();
      }
    });
  }

  getCurrentSelection() {
    const selection = {};
    const quantityInputs = this.bundleForm.querySelectorAll(this.SELECTORS.quantityInput);

    quantityInputs.forEach(input => {
      const quantity = parseInt(input.value) || 0;
      if (quantity > 0) {
        const match = input.id.match(/biscuits-product-quantity-\d+-(\d+)/);
        if (match) {
          selection[match[1]] = quantity;
        }
      }
    });

    return selection;
  }

  updateURL() {
    if (!this.isInitialized) return;

    const selection = this.getCurrentSelection();
    const url = new URL(window.location);

    url.searchParams.delete('bundle');

    if (Object.keys(selection).length > 0) {
      const bundleData = Object.entries(selection)
        .map(([variantId, quantity]) => `${variantId}:${quantity}`)
        .join(',');
      url.searchParams.set('bundle', bundleData);
    }

    window.history.replaceState({}, '', url.toString());
  }

  decodeSelection(bundleData) {
    const selection = {};
    if (!bundleData) return selection;

    try {
      bundleData.split(',').forEach(pair => {
        const [variantId, quantity] = pair.split(':');
        if (variantId && quantity) {
          selection[variantId] = parseInt(quantity);
        }
      });
    } catch (error) {
      console.error('Error decoding bundle data:', error);
    }

    return selection;
  }

  restoreSelectionFromURL() {
    const bundleData = this.urlParams.get('bundle');
    if (!bundleData) return;

    const selection = this.decodeSelection(bundleData);
    this.debug('Restoring selection from URL:', selection);

    // Wait for bundle form to be fully loaded
    setTimeout(() => {
      this.applySelection(selection);
    }, 1500);
  }

  applySelection(selection) {
    this.debug('Applying selection:', selection);

    Object.entries(selection).forEach(([variantId, quantity], index) => {
      // Try multiple selectors to find the input
      let input = this.bundleForm.querySelector(`input[id*="quantity-"][id*="-${variantId}"]`) ||
        this.bundleForm.querySelector(`input[id$="-${variantId}"]`) ||
        this.bundleForm.querySelector(`input[id*="${variantId}"]`);

      if (!input) {
        console.warn(`Input not found for variant ${variantId}`);
        return;
      }

      const quantityContainer = input.closest(this.SELECTORS.quantity);
      const plusButton = quantityContainer?.querySelector(this.SELECTORS.plus);
      const minusButton = quantityContainer?.querySelector(this.SELECTORS.minus);

      this.debug(`Variant ${variantId}: input found=${!!input}, plus button found=${!!plusButton}, quantity=${quantity}`);

      if (input && plusButton && quantity > 0) {
        const currentValue = parseInt(input.value) || 0;

        // Add delay between different variants
        const baseDelay = index * 200;

        setTimeout(() => {
          // Reset current value if needed
          if (currentValue > 0 && minusButton) {
            for (let i = 0; i < currentValue; i++) {
              setTimeout(() => minusButton.click(), i * 100);
            }
          }

          // Apply new quantity after reset
          setTimeout(() => {
            for (let i = 0; i < quantity; i++) {
              setTimeout(() => {
                this.debug(`Clicking + for variant ${variantId}, step ${i + 1}/${quantity}`)
                plusButton.click();
              }, i * 200);
            }
          }, currentValue * 100 + 500);
        }, baseDelay);

      } else if (input && quantity > 0) {
        // Fallback: direct value setting
        this.debug(`Fallback: setting value directly for variant ${variantId}`);
        setTimeout(() => {
          input.value = quantity;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }, index * 100);
      }
    });
  }

  addShareButton() {
    let actionsContainer = false;
    if (document.querySelector('.shopify-section--main-product')) {
      actionsContainer = this.bundleForm.closest('.shopify-section--main-product').querySelector('.Block-Type-buy_buttons');
    } else if (document.querySelector('.shopify-section--featured-product')) {
      actionsContainer = this.bundleForm.closest('.shopify-section--featured-product').querySelector('.Block-Type-buy_buttons');
    }
    if (!actionsContainer) return;

    const shareContainer = document.createElement('div');
    shareContainer.className = 'bundle-share-container';

    const shareButton = document.createElement('button');
    shareButton.type = 'button';
    shareButton.className = 'bundle-share-button button button--primary';
    shareButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.65 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"/>
      </svg><span class="button-text">${this.translations.shareButton}</span>
    `;

    shareButton.addEventListener('click', () => {
      this.copyBundleURL(shareButton);
    });

    shareContainer.append(shareButton);
    actionsContainer.append(shareContainer);
  }

  async copyBundleURL(button) {
    const selection = this.getCurrentSelection();

    if (Object.keys(selection).length === 0) {
      this.showButtonStatus(button, this.translations.selectProducts);
      return;
    }

    const url = new URL(window.location);
    const bundleData = Object.entries(selection)
      .map(([variantId, quantity]) => `${variantId}:${quantity}`)
      .join(',');
    url.searchParams.set('bundle', bundleData);

    const parentBundle = button.closest('.product__info--bundle');
    let discountUrl = '';
    if (parentBundle) {
      const discountButton = parentBundle.querySelector('.product-form__biscuits-copy-button');
      if (discountButton) {
        const dataUrl = discountButton.getAttribute('data-discount-url');
        if (dataUrl && dataUrl.trim() !== '') {
          discountUrl = dataUrl.trim();
        }
      }
    }

    this.showButtonStatus(button, this.translations.copying);

    try {
      let finalUrl;

      if (discountUrl) {
        const currentPath = url.pathname + url.search;
        finalUrl = `${window.location.origin}${discountUrl}?redirect=${currentPath.replace("?", "&")}`;
      } else {
        finalUrl = url.toString();
      }
      await navigator.clipboard.writeText(finalUrl);
      this.showButtonStatus(button, this.translations.copied);
    } catch (error) {
      this.showButtonStatus(button, this.translations.copyError);
    }
  }

  showButtonStatus(button, message) {
    const buttonText = button.querySelector('.button-text');
    const originalText = this.translations.shareButton;

    if (buttonText) {
      buttonText.textContent = message;
      buttonText.classList.add('active');

      setTimeout(() => {
        buttonText.textContent = originalText;
        buttonText.classList.remove('active');
      }, 3000);
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  debug(...args) {
    if (this.debugMode) console.log('[BundleURLManager]', ...args);
  }
}

if (window.location.pathname.includes('bundle-builder') || document.querySelector('biscuits-bundle-form')) {
  window.bundleManager = new BundleURLManager();
}